import {
  ARCADE_CUT_DIR_TOLERANCE_DEFAULT_DEG,
  ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
  ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
  ARCADE_MIN_SPEED_DEFAULT_MPS,
  ARCADE_MIN_SPEED_MAX_MPS,
  ARCADE_MIN_SPEED_MIN_MPS,
  ARCADE_SIZE_FACTOR_DEFAULT,
  ARCADE_SIZE_FACTOR_MAX,
  ARCADE_SIZE_FACTOR_MIN,
  ARCADE_SPAWN_DELAY_DEFAULT_MS,
  ARCADE_SPAWN_DELAY_MAX_MS,
  ARCADE_SPAWN_DELAY_MIN_MS,
  ARCADE_TTL_DEFAULT_MS,
  ARCADE_TTL_MAX_MS,
  ARCADE_TTL_MIN_MS,
} from '../config/constants';
import { getSetting, setSetting, type UserSettings } from '../settings/userSettings';

/**
 * Typed adapter over the settings store for the live-tunable arcade ("Sfida")
 * parameters — same pattern as `targetsSettings.ts`. Range validation lives here;
 * persistence (and cloud sync) is delegated to `userSettings`.
 */
type NumericKey = {
  [K in keyof UserSettings]: UserSettings[K] extends number ? K : never;
}[keyof UserSettings];

function loadNumber(key: NumericKey, min: number, max: number, def: number): number {
  const n = getSetting(key);
  if (!isFinite(n) || n < min || n > max) return def;
  return n;
}

function saveNumber(key: NumericKey, v: number): void {
  setSetting(key, v);
}

/** Time (ms) a target stays alive before auto-missing. */
export function loadArcadeTtl(): number {
  return loadNumber('arcadeTtl', ARCADE_TTL_MIN_MS, ARCADE_TTL_MAX_MS, ARCADE_TTL_DEFAULT_MS);
}

export function saveArcadeTtl(v: number): void {
  saveNumber('arcadeTtl', v);
}

/** Delay (ms) between a target resolving and the next spawning. */
export function loadArcadeSpawnDelay(): number {
  return loadNumber(
    'arcadeSpawnDelay',
    ARCADE_SPAWN_DELAY_MIN_MS,
    ARCADE_SPAWN_DELAY_MAX_MS,
    ARCADE_SPAWN_DELAY_DEFAULT_MS,
  );
}

export function saveArcadeSpawnDelay(v: number): void {
  saveNumber('arcadeSpawnDelay', v);
}

/** Angular tolerance (deg) on the required cut direction. */
export function loadArcadeCutTolerance(): number {
  return loadNumber(
    'arcadeCutTolerance',
    ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
    ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
    ARCADE_CUT_DIR_TOLERANCE_DEFAULT_DEG,
  );
}

export function saveArcadeCutTolerance(v: number): void {
  saveNumber('arcadeCutTolerance', v);
}

/** Disc size multiplier (mesh + hit sphere). */
export function loadArcadeSizeFactor(): number {
  return loadNumber(
    'arcadeSizeFactor',
    ARCADE_SIZE_FACTOR_MIN,
    ARCADE_SIZE_FACTOR_MAX,
    ARCADE_SIZE_FACTOR_DEFAULT,
  );
}

export function saveArcadeSizeFactor(v: number): void {
  saveNumber('arcadeSizeFactor', v);
}

/** Cut (custom): minimum tip speed (m/s) for a slice to count. */
export function loadArcadeMinSpeed(): number {
  return loadNumber(
    'arcadeMinSpeed',
    ARCADE_MIN_SPEED_MIN_MPS,
    ARCADE_MIN_SPEED_MAX_MPS,
    ARCADE_MIN_SPEED_DEFAULT_MPS,
  );
}

export function saveArcadeMinSpeed(v: number): void {
  saveNumber('arcadeMinSpeed', v);
}
